import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { DynamicTable } from "@cayuse/shared-react.components.dynamic-table";
import FormattedValue from "@cayuse/shared-react.components.formatted-value";
import { LinkToMilestone } from "@cayuse/shared-react.components.link-to-milestone";
import { SetView } from "@cayuse/shared-react.components.set-view";
import { MilestoneProgressView, TaskTitleCell } from "@cayuse/shared-react.components.task-form";
import { CustomDropdown } from "@cayuse/shared-react.custom-dropdown";
import { uniqBy } from "lodash";

import SPLink from "components/SPLink";
import {
	AWARDS,
	PROJECTS,
	PROPOSALS,
	SUBAWARDS,
	ROLE_SP_AWARD_ADMINISTRATOR,
	ROLE_SP_PROPOSAL_ADMINISTRATOR,
	ROLE_SP_SUBAWARD_ADMINISTRATOR,
	ROLE_SP_SYSTEM_ADMINISTRATOR,
} from "globals/constants";
import { TASK_ACTIVE_COLUMN, TASK_CATEGORIES, TASK_FILTER, TASK_TYPES } from "globals/constants/task";
import { useDictionary, usePagination, useHasRoles, useActiveColumns } from "hooks";
import { clearTaskList, getProjectTasks, loadMilestoneOptions, setFilters, setPaging, setSort } from "store/actions/tasks/taskList";
import { taskListSelectors } from "store/selectors";

import SidebarFilters from "./SidebarFilters";
import styles from "./styles.css";
import { AssignedToCell, CreatedByCell } from "./TaskTableCustomCells";

/**
 *  @property tasksFor, values: proposal. award
 * */
export function TaskListView ({ getTaskPagePath, instanceId, tasksFor }) {
	const { pageLimit, pageOffset, setPageLimit, setPageNumber } = usePagination("tasks", setPaging);
	const dictionary = useDictionary("tasks");

	const {
		[ROLE_SP_SYSTEM_ADMINISTRATOR]: isSystemAdmin,
		[ROLE_SP_AWARD_ADMINISTRATOR]: isAwardAdmin,
		[ROLE_SP_PROPOSAL_ADMINISTRATOR]: isProposalAdmin,
		[ROLE_SP_SUBAWARD_ADMINISTRATOR]: isSubawardAdmin
	} = useHasRoles([
		ROLE_SP_SYSTEM_ADMINISTRATOR,
		ROLE_SP_AWARD_ADMINISTRATOR,
		ROLE_SP_PROPOSAL_ADMINISTRATOR,
		ROLE_SP_SUBAWARD_ADMINISTRATOR
	]);

	const /**@type boolean*/isAdmin = isSystemAdmin
    || (tasksFor === PROJECTS && (isAwardAdmin || isProposalAdmin || isSubawardAdmin))
    || (tasksFor === PROPOSALS && isProposalAdmin)
    || (tasksFor === AWARDS && isAwardAdmin)
    || (tasksFor === SUBAWARDS && isSubawardAdmin);

	const tableRef = useRef(null);
	const history = useHistory();
	const isLoading = useSelector(taskListSelectors.isFetching);
	const isRefresh = useSelector(taskListSelectors.isRefresh);
	const { items, sort, totalItems, filters } = useSelector(taskListSelectors.content);
	const { columns: activeColumns, updateActiveColumns } = useActiveColumns("TASK");

	const [ selectedTasks, setSelectedTasks ] = useState([]);


	useEffect(() => {
		clearTaskList();
		getProjectTasks(instanceId);
	}, [ instanceId ]);

	const handleSortChange = (column, direction) => {
		setSort(column, direction);
		getProjectTasks(instanceId, { isRefresh: true });
	};

	const onPageChange = pageNumber => {
		setPageNumber(pageNumber);
		getProjectTasks(instanceId, { isRefresh: true });
	};

	const onPageLimitChange = pageLimit => {
		setPageLimit(pageLimit);
		getProjectTasks(instanceId, { isRefresh: true });
	};

	const onFilterChange = filterValue => {
		setPageNumber(1);
		setFilters(filterValue);
		getProjectTasks(instanceId, { isRefresh: true });
	};

	const getLoadingMessage = () => {
		if (isLoading) {
			return "Searching...";
		} else if (!isLoading && totalItems > 0) {
			return `${totalItems} results returned`;
		} else if (!isLoading && totalItems === 0) {
			return "No results returned";
		}
	};

	return (
		<>
			<span role="alert" className="accessibility-text" aria-live="polite" aria-atomic="true">
				{getLoadingMessage()}
			</span>
			<div className={styles.taskListHeaderContainer}>
				{isAdmin ? (
					<LinkToMilestone
						tasks={selectedTasks}
						onSuccess={async () => {
							await new Promise(resolve => setTimeout(resolve, 1800));
							setSelectedTasks([]);
							tableRef.current.resetSelection();
							getProjectTasks(instanceId, { isRefresh: true });
						}}
						loadMilestoneOptions={searchText =>
							loadMilestoneOptions({ searchText, submissionId: instanceId })
						}
						tableRenderCell={cell => {
							if (cell.column.id === "to") {
								return <AssignedToCell {...cell.row} fallback={cell.value} />;
							}
						}}
					/>
				) : null}
				<div role="application" aria-roledescription="dropdown">
					<CustomDropdown
						customClassname={styles.createEntityButton}
						options={[
							{
								label: dictionary.createNewDropdown.taskOption,
								value: "task",
								onClick: value => {
									history.push(getTaskPagePath(null, value));
								}
							},
							{
								label: dictionary.createNewDropdown.milestoneOption,
								value: "milestone",
								onClick: value => {
									history.push(getTaskPagePath(null, value));
								}
							},
						]}
						isCustomStyling
						label={dictionary.createNewDropdown.label}
					/>
				</div>
				<SetView
					initialColumns={TASK_ACTIVE_COLUMN}
					columns={activeColumns}
					onChangeColumns={updateActiveColumns}
				/>
			</div>
			<div className={styles.taskListBody}>
				<SidebarFilters
					filters={filters}
					onFilterChange={onFilterChange}
					filterConfig={TASK_FILTER}
				/>
				<DynamicTable
					className={styles.taskListTable}
					ref={tableRef}
					isLoading={isLoading}
					isRefresh={isRefresh}
					headers={activeColumns}
					rows={items}
					totalItems={totalItems}
					pageSize={pageLimit}
					pageOffset={pageOffset}
					onPageChange={onPageChange}
					onPageSizeChange={onPageLimitChange}
					onRowClick={row => {
						history.push(getTaskPagePath(row.id, row.category === TASK_CATEGORIES.CAYUSE_SP_MILESTONE ? "milestone" : "task"));
					}}
					onSortChange={handleSortChange}
					sortColumn={sort.column}
					sortDirection={sort.direction}
					isSelectable={isAdmin}
					selectableBy={row => {
						row._isSelectable = row.category === TASK_CATEGORIES.CAYUSE_SP_AD_HOC;
						row._isDisabledForSelection = Boolean(row.milestoneId);
						row._isSelected = Boolean(selectedTasks.find(t => t.id === row.id));

						return row;
					}}
					onRowSelectionChange={({ rows, isSelected }) => {
						if (isSelected) {
							setSelectedTasks(prev => uniqBy(prev.concat(rows), "id"));
						} else {
							setSelectedTasks(prev => {
								return prev.filter(r => !rows.some(row => row.id === r.id));
							});
						}
					}}
					renderCell={({ row, column, value }) => {
						if (column.id === "taskCounter") {
							const type = row.category === TASK_CATEGORIES.CAYUSE_SP_MILESTONE ? "milestone" : "task";

							return (
								<SPLink
									to={getTaskPagePath(row.id, type)}
									aria-label={`Open ${row.taskCounter} ${TASK_TYPES[row.category]}`}
									title={`Open ${row.taskCounter} ${TASK_TYPES[row.category]}`}
									onClick={e => !(process.env.NODE_ENV === "test") && e.stopPropagation()}
									data-testid={`${type}-counter-link`}
								>
									{row.taskCounter}
								</SPLink>
							);
						} else if (column.id === "milestoneCounter") {
							if (!row.milestoneId) {
								return null;
							}

							return (
								<SPLink
									to={getTaskPagePath(row.milestoneId, "milestone")}
									aria-label={`Open ${row.milestoneCounter} ${TASK_TYPES[row.category]}`}
									title={`Open ${row.milestoneCounter} ${TASK_TYPES[row.category]}`}
									onClick={e => !(process.env.NODE_ENV === "test") && e.stopPropagation()}
									data-testid="milestone-counter-link"
								>
									{row.milestoneCounter}
								</SPLink>
							);
						} else if (column.id === "task-type") {
							return TASK_TYPES[row.category];
						} else if (column.id === "from") {
							return <CreatedByCell {...row} fallback={value} />;
						} else if (column.id === "assigned-to") {
							return <AssignedToCell {...row} fallback={value} />;
						} else if (column.id === "createdOn" || column.id === "lastUpdated" || column.id === "dueOn" || column.id === "internalDueDate") {
							return <FormattedValue value={value} type="date" fallback="--" />;
						} else if (column.id === "progress" && row.category === TASK_CATEGORIES.CAYUSE_SP_MILESTONE) {
							return <MilestoneProgressView label="" completePercent={row.percentComplete} />;
						} else if (column.id === "task") {
							return <TaskTitleCell title={row["task"]} externalDueDate={row.dueOn} />;
						}

						return value;
					}}
				/>
			</div>
		</>
	);
}
